import { inject, computed } from '@angular/core';
import {
    signalStore,
    withState,
    withComputed,
    withMethods,
    patchState,
} from '@ngrx/signals';
import { LoginOrganizationSite } from 'api/models';
import { CurrentSiteRole } from '@app/shared/models/authentication/session';
import { SearchStore } from '@app/stores/searchStore';

export interface AppState {
    currentOrganizationSite: LoginOrganizationSite | null;
    currentSiteRole: CurrentSiteRole | null;
    availableOrganizationSites: LoginOrganizationSite[];
    isLoading: boolean;
    error: string | null;
    lastUpdated: number;
    selectedLanguage: string;
    isLoggedIn: boolean;
    isTourPlanMode: boolean;
    isUploadMode: boolean;
    // Location filter state - key is userRole, value is array of selected site keys
    selectedLocations: { [key: string]: string[] };
    // Show closed filter state - global boolean for all views
    showClosed: boolean;
    // Dialog state - track when table dialogs are open to prevent pagination conflicts
    isSharePermissionsDialogOpen: boolean;
    // UI visibility / mode flags migrated from services
    closeButtonVisibility: boolean;
    maintenanceMode: boolean;
}

const loadShowClosedFromStorage = (): boolean => {
    try {
        const stored = localStorage.getItem('showClosed');
        return stored !== null ? JSON.parse(stored) : true; // Default to true
    } catch {
        return true; // Default to true if parsing fails
    }
};

export const initialState: AppState = {
    currentOrganizationSite: null,
    currentSiteRole: null,
    availableOrganizationSites: [],
    isLoading: false,
    error: null,
    lastUpdated: 0,
    selectedLanguage: 'de',
    isLoggedIn: false,
    isTourPlanMode: false,
    isUploadMode: false,
    selectedLocations: {},
    showClosed: loadShowClosedFromStorage(),
    isSharePermissionsDialogOpen: false,
    closeButtonVisibility: true,
    maintenanceMode: false,
};

// Helper to map role string to base user role for location keys
function mapRoleToUserRole(role: string | undefined): string | null {
    if (!role) return null;
    if (role === 'carrier' || role === 'remote_carrier') return 'carrier';
    if (role === 'consignor' || role === 'remote_consignor') return 'consignor';
    if (role === 'consignee' || role === 'remote_consignee') return 'consignee';
    return null;
}

export const AppStore = signalStore(
    { providedIn: 'root' },
    withState<AppState>(initialState),
    withComputed(store => ({
        currentOrganizationSiteKey: computed(
            () => store.currentOrganizationSite()?._key || null
        ),
        currentSiteName: computed(
            () =>
                store.currentSiteRole()?.siteInfo?.fullSiteName || null
        ),
        currentRole: computed(
            () => store.currentSiteRole()?.role || null
        ),
        isCurrentSiteAdmin: computed(() => {
            const siteRole = store.currentSiteRole();
            return (
                siteRole?.role === 'SiteAdmin' ||
                siteRole?.role === 'OrganizationAdmin'
            );
        }),
    })),
    withMethods((store, searchStore = inject(SearchStore)) => {
        // Internal helper for setting locations with tour plan mode override
        const _setSelectedLocations = (
            userRole: string,
            locationKeys: string[]
        ) => {
            // When in tour plan mode, ignore location changes and force current site only
            if (store.isTourPlanMode()) {
                const currentSiteKey =
                    store.currentOrganizationSite()?._key || null;
                if (currentSiteKey) {
                    locationKeys = [currentSiteKey];
                }
            }

            // If no locations are selected, automatically add the current organization site key
            let finalLocationKeys = locationKeys;
            if (locationKeys.length === 0) {
                const currentSiteKey =
                    store.currentOrganizationSite()?._key || null;
                if (currentSiteKey) {
                    finalLocationKeys = [currentSiteKey];
                }
            }

            patchState(store, state => ({
                selectedLocations: {
                    ...state.selectedLocations,
                    [userRole]: finalLocationKeys,
                },
                lastUpdated: Date.now(),
            }));
        };

        const _setIsTourPlanMode = (isTourPlanMode: boolean) => {
            patchState(store, {
                isTourPlanMode,
                lastUpdated: Date.now(),
            });

            // When entering tour plan mode, force selection to current site only
            if (isTourPlanMode) {
                const currentSiteKey =
                    store.currentOrganizationSite()?._key || null;
                const currentSiteRole = store.currentSiteRole();
                if (currentSiteKey && currentSiteRole?.role) {
                    const userRole = mapRoleToUserRole(currentSiteRole.role);
                    if (userRole) {
                        _setSelectedLocations(userRole, [currentSiteKey]);
                    }
                }
            }
        };

        return {
            // --- Basic setters ---
            setAppLoading(loading: boolean): void {
                patchState(store, { isLoading: loading });
            },

            setAppError(error: string | null): void {
                patchState(store, { error });
            },

            setCurrentOrganizationSite(
                site: LoginOrganizationSite | null
            ): void {
                patchState(store, {
                    currentOrganizationSite: site,
                    lastUpdated: Date.now(),
                });
            },

            setCurrentSiteRole(siteRole: CurrentSiteRole | null): void {
                const nextUserRole = mapRoleToUserRole(siteRole?.role);
                patchState(store, {
                    currentSiteRole: siteRole,
                    // Tour plan UI is carrier-only and must close when role changes away.
                    isTourPlanMode:
                        nextUserRole === 'carrier'
                            ? store.isTourPlanMode()
                            : false,
                    lastUpdated: Date.now(),
                });
            },

            setAvailableOrganizationSites(
                sites: LoginOrganizationSite[]
            ): void {
                patchState(store, {
                    availableOrganizationSites: sites,
                    lastUpdated: Date.now(),
                });
            },

            setSelectedLanguage(language: string): void {
                patchState(store, {
                    selectedLanguage: language,
                    lastUpdated: Date.now(),
                });
            },

            setIsLoggedIn(isLoggedIn: boolean): void {
                patchState(store, {
                    isLoggedIn,
                    lastUpdated: Date.now(),
                });
            },

            // --- Tour plan mode ---
            setIsTourPlanMode: _setIsTourPlanMode,

            toggleTourPlanMode(): void {
                const newTourPlanMode = !store.isTourPlanMode();
                _setIsTourPlanMode(newTourPlanMode);
            },

            // --- Upload mode ---
            setIsUploadMode(isUploadMode: boolean): void {
                patchState(store, {
                    isUploadMode,
                    lastUpdated: Date.now(),
                });
            },

            toggleUploadMode(): void {
                patchState(store, state => ({
                    isUploadMode: !state.isUploadMode,
                    lastUpdated: Date.now(),
                }));
            },

            // --- Cross-store: site + role update ---
            updateCurrentOrganizationSiteAndRole(
                site: LoginOrganizationSite,
                siteRole: CurrentSiteRole
            ): void {
                const nextUserRole = mapRoleToUserRole(siteRole?.role);
                patchState(store, state => ({
                    currentOrganizationSite: site,
                    currentSiteRole: siteRole,
                    // Close tour plan when leaving carrier context.
                    isTourPlanMode:
                        nextUserRole === 'carrier' ? state.isTourPlanMode : false,
                    // Clear all selected locations when switching sites
                    selectedLocations: {},
                    // Keep show closed filter when switching sites
                    showClosed: state.showClosed,
                    lastUpdated: Date.now(),
                }));

                // Set only the new site as selected for the current role
                if (siteRole?.role) {
                    const userRole = mapRoleToUserRole(siteRole.role);

                    if (userRole && site._key) {
                        _setSelectedLocations(userRole, [site._key]);

                        // Trigger data refresh to get updated dataset for the new site
                        searchStore.triggerDataRefresh();
                    }
                }
            },

            // --- Clear state ---
            clearAppState(): void {
                patchState(store, state => ({
                    ...initialState,
                    selectedLanguage: state.selectedLanguage, // Keep language preference
                }));
            },


            // --- Location filters ---
            setSelectedLocations: _setSelectedLocations,

            getSelectedLocationKeys(userRole: string): string[] {
                return store.selectedLocations()[userRole] || [];
            },

            hasSelectedLocations(userRole: string): boolean {
                return (
                    userRole in store.selectedLocations() &&
                    store.selectedLocations()[userRole].length > 0
                );
            },

            addLocationToSelection(
                userRole: string,
                locationKey: string
            ): void {
                const currentLocations =
                    store.selectedLocations()[userRole] || [];
                if (!currentLocations.includes(locationKey)) {
                    _setSelectedLocations(userRole, [
                        ...currentLocations,
                        locationKey,
                    ]);
                }
            },

            initializeSelectedLocations(
                userRole: string,
                currentOrganizationSiteKey: string
            ): void {
                // Initialize with current location if no selection exists
                const hasLocations =
                    userRole in store.selectedLocations() &&
                    store.selectedLocations()[userRole].length > 0;
                if (!hasLocations) {
                    _setSelectedLocations(userRole, [
                        currentOrganizationSiteKey,
                    ]);
                }
            },

            // --- Show closed (localStorage-backed) ---
            setShowClosed(showClosed: boolean): void {
                try {
                    localStorage.setItem(
                        'showClosed',
                        JSON.stringify(showClosed)
                    );
                } catch (error) {
                    console.warn(
                        'Failed to save showClosed to localStorage:',
                        error
                    );
                }

                patchState(store, {
                    showClosed,
                    lastUpdated: Date.now(),
                });
            },

            toggleShowClosed(): void {
                const newShowClosed = !store.showClosed();

                try {
                    localStorage.setItem(
                        'showClosed',
                        JSON.stringify(newShowClosed)
                    );
                } catch (error) {
                    console.warn(
                        'Failed to save showClosed to localStorage:',
                        error
                    );
                }

                patchState(store, {
                    showClosed: newShowClosed,
                    lastUpdated: Date.now(),
                });
            },

            // --- Dialog state ---
            setSharePermissionsDialogOpen(isOpen: boolean): void {
                patchState(store, {
                    isSharePermissionsDialogOpen: isOpen,
                    lastUpdated: Date.now(),
                });
            },

            // --- UI visibility / mode ---
            setCloseButtonVisibility(visible: boolean): void {
                patchState(store, { closeButtonVisibility: visible });
            },

            setMaintenanceMode(mode: boolean): void {
                patchState(store, { maintenanceMode: mode });
            },
        };
    })
);

